/**
 * A form template inserted onto the Designer canvas is ONE unit.
 *
 * THE PROBLEM IT SOLVES
 * ---------------------
 * `addFormTemplate` used to be `template.components.forEach((c) => onAdd(c))`, so after
 * the insert the concept "template" did not exist in the data at all — which is why the
 * owner's ask (circle it, delete it as a whole) had nothing to attach to.
 *
 * OWNER DECISION 2026-08-27 — the group is a HARD CONTAINER, not merely a delete unit:
 *
 *   > 整组删除，不支持表单内组件单独编辑。拖动也只能是整个组件模板拖动和删除。
 *
 * So all three per-field affordances are withdrawn inside a group: delete, edit, and
 * reorder. That is the stronger reading, and it makes the invariant cheap to assert —
 * the canvas must offer ZERO per-field controls for a grouped row, which is a COUNT
 * rather than the absence of one handler.
 *
 * WHY THE METADATA LIVES IN `properties`
 * --------------------------------------
 * `serializeFormSteps` whitelists `id · component_id · type · label · required ·
 * properties` and passes `properties` through WHOLE; `validate_form_schema` does no
 * unknown-key check inside it. So these three keys round-trip with no serializer
 * change, no backend schema change, no migration — measured, not assumed. The data is
 * therefore persisted from day one, which is the "increment without rework" property
 * asked for: making the RUNTIME renderers group visually later is each reading three
 * keys, not redesigning anything.
 */

/** JSONB property names stamped inside each inserted field's `properties`.
 *  Named `_PROP`, not `_KEY`: `orgScopedStorage.test.ts` scans for constants whose
 *  declaration mentions "key" and requires them to be org-scoped storage keys, and
 *  these are neither storage nor keys — they are form-schema property names. */
export const GROUP_ID_PROP = 'group_id';
export const GROUP_LABEL_PROP = 'group_label';
export const GROUP_TEMPLATE_ID_PROP = 'group_template_id';

export interface GroupStamp {
  group_id: string;
  /** A SNAPSHOT of the template's name at insert time, for the same reason `to_shift`
   *  is stored beside `to_shift_id`: renaming the template later must not rewrite what
   *  was inserted. */
  group_label: string;
  /** Provenance, kept separately so it and the snapshot cannot disagree. */
  group_template_id: string;
}

/**
 * Minted PER INSERT, so inserting the same template twice produces TWO groups rather
 * than one merged group. Somebody inserting it twice is deliberately collecting two
 * sets of the same information, and merging would silently destroy that.
 */
export function mintGroupStamp(template: { id: string; name: string }): GroupStamp {
  const unique = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  return {
    group_id: `grp-${unique}`,
    group_label: template.name,
    group_template_id: template.id,
  };
}

interface HasProperties {
  id: string;
  properties?: Record<string, unknown> | null;
}

export function groupIdOf(step: HasProperties): string | null {
  const raw = step.properties?.[GROUP_ID_PROP];
  return typeof raw === 'string' && raw !== '' ? raw : null;
}

export function groupLabelOf(step: HasProperties): string {
  const raw = step.properties?.[GROUP_LABEL_PROP];
  return typeof raw === 'string' ? raw : '';
}

/** A canvas row: either one loose field, or a whole group. */
export interface CanvasBlock<T extends HasProperties> {
  /** `draggableId` for the block. A group's own id, or the single step's id. */
  key: string;
  groupId: string | null;
  label: string;
  steps: T[];
}

/**
 * Consecutive steps sharing a `group_id` collapse into one block.
 *
 * CONSECUTIVE, not "all with this id": a legacy definition could in principle hold the
 * same id in two places, and silently pulling them together would reorder a saved form.
 * A non-consecutive repeat therefore becomes a SECOND block, which renders honestly
 * rather than rearranging the tenant's data behind their back.
 */
export function toCanvasBlocks<T extends HasProperties>(steps: T[]): CanvasBlock<T>[] {
  const blocks: CanvasBlock<T>[] = [];
  steps.forEach((step) => {
    const groupId = groupIdOf(step);
    const last = blocks[blocks.length - 1];
    if (groupId !== null && last && last.groupId === groupId) {
      last.steps.push(step);
      return;
    }
    blocks.push({
      key: groupId === null ? step.id : `${groupId}::${step.id}`,
      groupId,
      label: groupId === null ? '' : groupLabelOf(step),
      steps: [step],
    });
  });
  return blocks;
}

/**
 * Move one BLOCK and return the resulting flat step order.
 *
 * Expressed as the full resulting order rather than as index arithmetic, because a
 * group move is not a single-item splice: the parent's old `onDragEnd` moved exactly
 * one element, so reusing it would tear a group apart on the first drag.
 */
export function reorderBlocks<T extends HasProperties>(
  steps: T[], fromBlock: number, toBlock: number,
): T[] {
  const blocks = toCanvasBlocks(steps);
  if (fromBlock < 0 || fromBlock >= blocks.length) return steps;
  if (toBlock < 0 || toBlock >= blocks.length) return steps;
  const next = Array.from(blocks);
  const [moved] = next.splice(fromBlock, 1);
  next.splice(toBlock, 0, moved);
  const flat: T[] = [];
  next.forEach((block) => block.steps.forEach((step) => flat.push(step)));
  return flat;
}

/** Every step id in the block a given step belongs to — what a group delete removes. */
export function groupMemberIds<T extends HasProperties>(steps: T[], stepId: string): string[] {
  const block = toCanvasBlocks(steps).find(
    (candidate) => candidate.steps.some((step) => step.id === stepId),
  );
  return block ? block.steps.map((step) => step.id) : [stepId];
}
